<script setup>
import { Receipt, MapPin, History, ShieldCheck, Check, Store, PackageCheck } from 'lucide-vue-next'
import StoreButtons from './StoreButtons.vue'
import appPreview from '../../assets/img/app-llevo-screen.webp'

const benefits = [
  {
    icon: Receipt,
    title: 'Cotiza y pide en segundos',
    description: 'Solo ingresa el origen y destino, mira la tarifa exacta por adelantado y confirma. Sin sorpresas, sin esperas y sin formularios eternos.'
  },
  {
    icon: MapPin,
    title: 'Seguimiento GPS en vivo',
    description: 'Se acabaron las dudas. Mira en tiempo real cómo avanza tu paquete en el mapa y conoce el estado de la entrega minuto a minuto.'
  },
  {
    icon: History,
    title: 'Historial y reenvíos rápidos',
    description: 'Accede a todos tus envíos pasados, repite una ruta frecuente con un solo toque y cuenta con soporte humano y local cuando lo necesites.'
  }
]
</script>

<template>
  <section id="app-usuarios" class="relative scroll-mt-24 overflow-hidden border-b border-white/10 bg-[#0a1120] py-24 text-white md:py-32">
    <div class="pointer-events-none absolute inset-0 bg-grid-dark opacity-40 mask-radial"></div>
    <div class="pointer-events-none absolute -right-40 top-1/3 h-[28rem] w-[28rem] rounded-full bg-[#f0611e]/10 blur-[130px]"></div>

    <div class="container relative z-10 mx-auto px-6 lg:px-12">
      <div class="grid items-center gap-14 lg:grid-cols-12 lg:gap-10">

        <!-- Copy -->
        <div v-reveal class="lg:col-span-7">
          <p class="text-[11px] font-black uppercase tracking-[0.18em] text-[#ff8a4c]">Para usuarios</p>

          <h2 class="mt-4 text-balance text-3xl font-black tracking-[-0.03em] sm:text-4xl md:text-5xl">
            Movemos tus envíos,
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-[#ff8a4c] via-[#f0611e] to-[#ffb38a]">bajo tu control.</span>
          </h2>

          <p class="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Solicita un motorizado desde tu celular y sigue su recorrido en el mapa. Rápido, seguro y sin perder tiempo
            enviando mensajes para coordinar.
          </p>

          <div class="mt-8 divide-y divide-white/10 border-y border-white/10">
            <div v-for="benefit in benefits" :key="benefit.title" class="flex gap-4 py-4">
              <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#c2410c]/20 text-[#ff8a4c]">
                <component :is="benefit.icon" :size="18" />
              </span>
              <div>
                <h3 class="text-sm font-black text-white">{{ benefit.title }}</h3>
                <p class="mt-1 text-sm leading-relaxed text-slate-400">{{ benefit.description }}</p>
              </div>
            </div>
          </div>

          <div class="mt-8">
            <p class="mb-3 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Descarga la app</p>
            <StoreButtons />
          </div>

          <div class="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-400">
            <span class="inline-flex items-center gap-1.5">
              <ShieldCheck :size="14" class="text-[#ff8a4c]" />
              Repartidores verificados
            </span>
            <span class="inline-flex items-center gap-1.5">
              <Check :size="14" class="text-[#ff8a4c]" />
              Soporte humano
            </span>
          </div>
        </div>

        <!-- Captura real de la app con badges animados -->
        <div v-reveal="{ delay: 140 }" class="relative flex justify-center lg:col-span-5">
          <div class="relative w-full max-w-[300px]">
            <div class="rounded-[2.8rem] border border-white/15 bg-[#0a1120] p-2.5 shadow-panel-dark">
              <div class="overflow-hidden rounded-[2.2rem] bg-[#0a1120]">
                <img
                  :src="appPreview"
                  alt="Pantalla de inicio de la app de LLEVO"
                  class="h-auto w-full"
                  loading="lazy"
                  decoding="async"
                >
              </div>
            </div>

            <span
              aria-hidden="true"
              class="animate-float-slow absolute -left-4 top-20 flex items-center gap-2 rounded-xl border border-white/15 bg-[#0e1729]/95 px-3 py-2 shadow-xl backdrop-blur"
            >
              <Store :size="14" class="text-[#ff8a4c]" />
              <span class="text-[9px] font-black uppercase tracking-[0.12em] text-white">Negocios</span>
            </span>

            <span
              aria-hidden="true"
              class="animate-float-slow absolute -right-4 top-44 flex items-center gap-2 rounded-xl border border-white/15 bg-[#0e1729]/95 px-3 py-2 shadow-xl backdrop-blur"
              style="animation-delay: 0.8s;"
            >
              <PackageCheck :size="14" class="text-emerald-300" />
              <span class="text-[9px] font-black uppercase tracking-[0.12em] text-white">Pedido en camino</span>
            </span>

            <span
              aria-hidden="true"
              class="animate-float-slow absolute -left-5 bottom-36 flex items-center gap-2 rounded-xl border border-white/15 bg-[#0e1729]/95 px-3 py-2 shadow-xl backdrop-blur"
              style="animation-delay: 1.6s;"
            >
              <MapPin :size="14" class="text-[#ff8a4c]" />
              <span class="text-[9px] font-black uppercase tracking-[0.12em] text-white">GPS en vivo</span>
            </span>

            <span
              aria-hidden="true"
              class="animate-float-slow absolute -right-3 bottom-10 flex items-center gap-2 rounded-xl border border-white/15 bg-[#0e1729]/95 px-3 py-2 shadow-xl backdrop-blur"
              style="animation-delay: 2.4s;"
            >
              <Receipt :size="14" class="text-[#ff8a4c]" />
              <span class="text-[9px] font-black uppercase tracking-[0.12em] text-white">Tarifa clara</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
